import React from 'react';
import { motion } from 'framer-motion';
import { 
  ScrollText, 
  Flame, 
  Sprout, 
  Home, 
  Handshake, 
  Stethoscope, 
  Zap,
  BookOpen
} from 'lucide-react';

const missions = [
    {
        title: "Evangelism",
        text: "To lead souls to Christ, to demonstrate the standard of Christian living, and to spread the Gospel of Jesus Christ.",
        verse: "Matt 28:19-20",
        icon: <Flame className="w-6 h-6" />,
        color: "text-amber-600",
        bg: "bg-amber-50"
    },
    {
        title: "Faith Formation",
        text: "To advance the spiritual growth of members by strengthening our Christian faith formation through biblical teachings and prayers.",
        verse: "Acts 2:42-47",
        icon: <Sprout className="w-6 h-6" />,
        color: "text-emerald-600",
        bg: "bg-emerald-50"
    },
    {
        title: "Social Transformation",
        text: "To place Jesus at the center of our homes and partner with other groups to transform our Nation through love.",
        verse: "Matt 25:31-46",
        icon: <Home className="w-6 h-6" />,
        color: "text-blue-600",
        bg: "bg-blue-50"
    },
    {
        title: "Ecumenical Unity",
        text: "To continue to promote peaceful and cordial relationships with other Dioceses to ensure common success.",
        verse: "John 17:21",
        icon: <Handshake className="w-6 h-6" />,
        color: "text-purple-600",
        bg: "bg-purple-50"
    },
    {
        title: "Health & Wellness",
        text: "To provide basic healthcare, diagnostic laboratories and mobile clinics to reach the rural areas of our Diocese.",
        verse: "3 John 1:2",
        icon: <Stethoscope className="w-6 h-6" />,
        color: "text-rose-600",
        bg: "bg-rose-50"
    },
    {
        title: "Youth Vision",
        text: "To promote a vibrant, dynamic, and committed Youth Forum capable of running with the vision of our Diocese.",
        verse: "Habakkuk 2:2-3",
        icon: <Zap className="w-6 h-6" />,
        color: "text-orange-600",
        bg: "bg-orange-50"
    },
];

const MissionStatement = () => {
    return (
        <section className="bg-[#fcfaf7] py-24 px-4 overflow-hidden">
            <div className="max-w-6xl mx-auto">
                
                {/* Section Header */}
                <div className="text-center mb-20">
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-center gap-2 mb-4"
                    >
                        <BookOpen className="w-5 h-5 text-amber-600" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-slate-500">Our Mandate</span>
                    </motion.div>
                    <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-6">
                        Mission Statement
                    </h2>
                    <p className="text-slate-500 font-serif italic text-lg max-w-2xl mx-auto leading-relaxed">
                        "The strategic blueprint for our spiritual expansion and social impact in Ibadan South."
                    </p>
                </div>

                {/* Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {missions.map((mission, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="group relative"
                        >
                            <div className="bg-white rounded-[2.5rem] p-8 h-full shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 flex flex-col">
                                {/* Icon & Title */}
                                <div className="flex items-center gap-4 mb-6">
                                    <div className={`p-4 rounded-2xl ${mission.bg} ${mission.color} transition-transform group-hover:scale-110 duration-500`}>
                                        {mission.icon}
                                    </div>
                                    <h3 className="font-serif font-bold text-lg text-slate-900">{mission.title}</h3>
                                </div>

                                {/* Body Text */}
                                <p className="text-slate-600 leading-relaxed font-serif mb-8 flex-grow">
                                    {mission.text}
                                </p>

                                {/* Verse Footer */}
                                <div className="flex items-center gap-2 pt-6 border-t border-slate-50">
                                    <ScrollText className="w-3 h-3 text-slate-300" />
                                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                                        {mission.verse}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Summary Quote */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    className="mt-20 text-center"
                >
                    <div className="inline-block p-1 bg-gradient-to-r from-amber-500/20 to-transparent rounded-full">
                        <div className="bg-white px-8 py-3 rounded-full shadow-sm">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em]">
                                Rooted in Scripture • Driven by Love
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default MissionStatement;